import { PortalPageContent, PortalPageContentHeader } from "@fero/layouts";
import { AppRoutes } from "@fero/utils";
import { Button, Stack, Typography } from "@mui/material";
import type { GridSortModel } from "@mui/x-data-grid";
import type {
  NominatedPurchaserDetailsView,
  NomPurchaserStorefrontConfigData,
  OrgSetStorefrontEnablementReq,
} from "adl-gen/ferovinum/app/api";
import { FormField } from "components/types/formik-types";
import {
  GridTable,
  GridTableColumnFactory,
} from "components/widgets/grid-table/grid-table";
import { useFormik } from "formik";
import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

interface StorefrontDashboardPageViewProps {
  storefrontConfigs: NomPurchaserStorefrontConfigData[];
  nominatedPurchasersDetailsViews: NominatedPurchaserDetailsView[];
  handleStorefrontEnablement: (
    configToSet: OrgSetStorefrontEnablementReq,
  ) => void;
}

interface ConfigsTableProps {
  purchasersFormField: FormField<ConfigsTableRowData[]>;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  onConfigLinkClick: (
    configFormField?: FormField<ConfigsTableRowData[]>,
    nominatedPurchaserId?: string,
  ) => void;
  handleStorefrontEnablement: (
    configToSet: OrgSetStorefrontEnablementReq,
  ) => void;
}

export interface ConfigsTableRowData {
  id?: string | number;
  configId: string;
  purchaserName: string;
  purchaserId: string;
  selected: boolean;
  alerts: string;
  enabled: boolean;
}

const PurchasersTable = ({
  purchasersFormField,
  sortModel,
  onSortModelChange,
  onConfigLinkClick,
  handleStorefrontEnablement,
}: ConfigsTableProps) => {
  const columnFactory = new GridTableColumnFactory<ConfigsTableRowData>();
  const columns = [
    columnFactory.makeCheckboxColumn({
      key: "selected",
      header: "Selected",
      getter: (row) => row.selected,
      onChange: (row, value) => {
        const updatedPurchasers = purchasersFormField.get()?.map((p) => {
          return p.configId === row.configId
            ? { ...p, selected: p.enabled ? value : p.selected }
            : p;
        });
        purchasersFormField.set(updatedPurchasers);
      },
    }),
    columnFactory.makeButtonColumn({
      key: "purchaserName",
      header: "Purchaser",
      buttonElement: (row) =>
        row.enabled ? (
          <Typography
            variant="hyperLinkBig"
            sx={{ textDecoration: "underline" }}
          >
            {row.purchaserName}
          </Typography>
        ) : (
          <Typography sx={{ fontWeight: "bold" }}>
            {row.purchaserName}
          </Typography>
        ),
      getter: (row) => row.purchaserName,
      onClick: (row) =>
        row.enabled ? onConfigLinkClick(undefined, row.configId) : null,
    }),
    columnFactory.makeToggleColumn({
      key: "enabled",
      header: "Store Enabled",
      getter: (row) => row.enabled,
      onChange: (row, value) => {
        const updatedPurchasers = purchasersFormField
          .get()
          ?.map((p) =>
            p.configId === row.configId ? { ...p, enabled: value } : p,
          );
        purchasersFormField.set(updatedPurchasers);
        handleStorefrontEnablement({
          nominatedPurchaserId: row.configId,
          enabled: value,
        });
      },
    }),
  ];

  return (
    <GridTable
      columns={columns}
      inputRowsField={purchasersFormField}
      tableHeight="auto"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      rowHeight={80}
    />
  );
};

export const StorefrontDashboardPageView = ({
  storefrontConfigs,
  nominatedPurchasersDetailsViews,
  handleStorefrontEnablement,
}: StorefrontDashboardPageViewProps) => {
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "name", sort: "asc" },
  ]);
  const history = useHistory();

  const onClickEditSelectedConfigs = useMemo(
    () =>
      (
        configFormField?: FormField<ConfigsTableRowData[]>,
        nominatedPurchaserId?: string,
      ) => {
        if (nominatedPurchaserId) {
          const configToUpdate = storefrontConfigs.find(
            (p) => p.nominatedPurchaserId === nominatedPurchaserId,
          );
          const purchaserDetails = nominatedPurchasersDetailsViews.find(
            (n) => n.nominatedPurchaserId === nominatedPurchaserId,
          );
          return history.push(AppRoutes.NewPurchaserAllocation, {
            selectedStorefrontConfigs: [configToUpdate],
            purchaserDetails: [purchaserDetails],
          });
        }

        const configsTableData = configFormField?.get() || [];
        const updatedStorefrontConfigs: NomPurchaserStorefrontConfigData[] =
          configsTableData
            .filter((row) => row.selected)
            .map((row) =>
              storefrontConfigs.find(
                (p) => p.nominatedPurchaserId === row.configId,
              ),
            )
            .filter(
              (config) => config !== null,
            ) as NomPurchaserStorefrontConfigData[];

        const updatedPurchaserDetails: (
          | NominatedPurchaserDetailsView
          | undefined
        )[] = updatedStorefrontConfigs
          .map((config) =>
            nominatedPurchasersDetailsViews.find(
              (n) => n.nominatedPurchaserId === config.nominatedPurchaserId,
            ),
          )
          .filter((details) => details !== undefined);

        history.push(AppRoutes.NewPurchaserAllocation, {
          selectedStorefrontConfigs: updatedStorefrontConfigs,
          purchaserDetails: updatedPurchaserDetails,
        });
      },
    [storefrontConfigs, history, nominatedPurchasersDetailsViews],
  );

  const configsRowData: ConfigsTableRowData[] = useMemo(
    () =>
      storefrontConfigs.map((p) => {
        return {
          configId: p.nominatedPurchaserId,
          purchaserName:
            nominatedPurchasersDetailsViews.find(
              (n) => n.nominatedPurchaserId === p.nominatedPurchaserId,
            )?.name || "",
          purchaserId: p.nominatedPurchaserId,
          selected: false,
          alerts: "",
          enabled: p.config.storefrontEnabled,
        };
      }),
    [storefrontConfigs, nominatedPurchasersDetailsViews],
  );

  const form = useFormik<{ configs: ConfigsTableRowData[] }>({
    initialValues: { configs: configsRowData },
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {},
  });

  const storefrontConfigsFormField = useMemo(
    () => new FormField<ConfigsTableRowData[]>(form, "configs"),
    [form],
  );

  const hasSelectedConfigs = useMemo(() => {
    const configs = storefrontConfigsFormField.get();
    return configs?.some((p) => p.selected);
  }, [storefrontConfigsFormField]);

  const selectAllPurchasers = () => {
    const updatedConfigs = storefrontConfigsFormField.get()?.map((p) => ({
      ...p,
      selected: p.enabled,
    }));
    storefrontConfigsFormField.set(updatedConfigs);
  };

  return (
    <PortalPageContent header={<PortalPageContentHeader title="Storefront" />}>
      <Stack spacing={2}>
        <Stack spacing={2} direction="row" alignItems="flex">
          <Button onClick={selectAllPurchasers}>Select All</Button>
          <Button
            onClick={() =>
              onClickEditSelectedConfigs(storefrontConfigsFormField)
            }
            disabled={!hasSelectedConfigs}
          >
            Edit selected purchaser allocations
          </Button>
        </Stack>
        <Stack>
          <PurchasersTable
            purchasersFormField={storefrontConfigsFormField}
            sortModel={sortModel}
            onSortModelChange={(newModel: GridSortModel) =>
              setSortModel(newModel)
            }
            onConfigLinkClick={onClickEditSelectedConfigs}
            handleStorefrontEnablement={handleStorefrontEnablement}
          />
        </Stack>
      </Stack>
    </PortalPageContent>
  );
};
